// TAMFARIS Modal Center — singleton API dipanggil dari mana saja (async).
// notify.alert / .success / .error / .warning / .confirm({ title, message, ... }) -> Promise<boolean>
// Confirm memakai promise: resolve(true) untuk primary, resolve(false) untuk secondary/dismiss.
import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, XCircle, Info, HelpCircle, X } from "lucide-react";

let _listeners = new Set();
let _seq = 0;

function _emit(cfg) {
  return new Promise((resolve) => {
    const id = ++_seq;
    const item = { id, resolve, ...cfg };
    _listeners.forEach((fn) => fn(item));
  });
}

function _pick(arg, extra) {
  if (arg == null) return {};
  if (typeof arg === "string") return { message: arg, ...(extra || {}) };
  return { ...arg, ...(extra || {}) };
}

export const notify = {
  alert:   (arg, extra) => _emit({ variant: "info",    ..._pick(arg, extra) }),
  info:    (arg, extra) => _emit({ variant: "info",    ..._pick(arg, extra) }),
  success: (arg, extra) => _emit({ variant: "success", ..._pick(arg, extra) }),
  error:   (arg, extra) => _emit({ variant: "error",   ..._pick(arg, extra) }),
  warning: (arg, extra) => _emit({ variant: "warning", ..._pick(arg, extra) }),
  confirm: (arg, extra) => _emit({ variant: "confirm", ..._pick(arg, extra) }),
  prompt:  (arg, extra) => _emit({ variant: "prompt",  ..._pick(arg, extra) }),
};

const VARIANT_META = {
  info:    { Icon: Info,          klass: "tam-modal-info",    defaultTitle: "Informasi",           defaultPrimary: "Mengerti" },
  success: { Icon: CheckCircle2,  klass: "tam-modal-success", defaultTitle: "Berhasil",            defaultPrimary: "Selesai" },
  error:   { Icon: XCircle,       klass: "tam-modal-error",   defaultTitle: "Terjadi kesalahan",   defaultPrimary: "Tutup" },
  warning: { Icon: AlertTriangle, klass: "tam-modal-warning", defaultTitle: "Perhatian",           defaultPrimary: "Mengerti" },
  confirm: { Icon: HelpCircle,    klass: "tam-modal-confirm", defaultTitle: "Konfirmasi",          defaultPrimary: "Ya, lanjutkan" },
  prompt:  { Icon: HelpCircle,    klass: "tam-modal-confirm", defaultTitle: "Masukkan keterangan", defaultPrimary: "Lanjutkan" },
};

export function ModalCenter() {
  const [queue, setQueue] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const current = queue[0] || null;
  const primaryRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const push = (item) => setQueue((q) => [...q, item]);
    _listeners.add(push);
    return () => _listeners.delete(push);
  }, []);

  useEffect(() => {
    if (!current) return;
    setInputValue(current.defaultValue || "");
    setInputError("");
    const t = setTimeout(() => {
      if (current.variant === "prompt" && inputRef.current) inputRef.current.focus();
      else if (primaryRef.current) primaryRef.current.focus();
    }, 40);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  useEffect(() => {
    if (!current) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(current.variant === "prompt" ? null : false);
      if (e.key === "Enter" && current.variant !== "prompt") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  const close = (result) => {
    if (!current) return;
    const cur = current;
    setQueue((q) => q.slice(1));
    try { cur.resolve(result); } catch { /* noop */ }
  };

  const submitPrompt = () => {
    const val = String(inputValue || "").trim();
    const min = Number(current.minLength || 0);
    if (current.required && !val) { setInputError("Kolom wajib diisi."); return; }
    if (min > 0 && val.length < min) { setInputError(`Minimal ${min} karakter.`); return; }
    close(val);
  };

  if (!current) return null;
  const meta = VARIANT_META[current.variant] || VARIANT_META.info;
  const Icon = meta.Icon;
  const isConfirm = current.variant === "confirm";
  const isPrompt = current.variant === "prompt";
  const title = current.title || meta.defaultTitle;
  const message = current.message || "";
  const primaryLabel = current.primaryLabel || meta.defaultPrimary;
  const secondaryLabel = current.secondaryLabel || ((isConfirm || isPrompt) ? "Batal" : null);
  const danger = !!current.danger;

  return (
    <div className="tam-modal-overlay" role="dialog" aria-modal="true" data-testid="tam-modal" onClick={() => close(isPrompt ? null : false)}>
      <div className={`tam-modal ${meta.klass}`} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="tam-modal-close" aria-label="Tutup" data-testid="tam-modal-close" onClick={() => close(isPrompt ? null : false)}>
          <X size={16}/>
        </button>
        <div className="tam-modal-icon" aria-hidden="true"><Icon size={26}/></div>
        <h3 className="tam-modal-title" data-testid="tam-modal-title">{title}</h3>
        {message && <p className="tam-modal-message" data-testid="tam-modal-message">{message}</p>}
        {current.details && <div className="tam-modal-details" data-testid="tam-modal-details">{current.details}</div>}
        {isPrompt && (
          <div className="tam-modal-input-wrap">
            {current.multiline
              ? <textarea
                  ref={inputRef}
                  className="tam-modal-input"
                  data-testid="tam-modal-input"
                  rows={3}
                  placeholder={current.placeholder || ""}
                  value={inputValue}
                  onChange={(e) => { setInputValue(e.target.value); setInputError(""); }}
                />
              : <input
                  ref={inputRef}
                  type={current.inputType || "text"}
                  className="tam-modal-input"
                  data-testid="tam-modal-input"
                  placeholder={current.placeholder || ""}
                  value={inputValue}
                  onChange={(e) => { setInputValue(e.target.value); setInputError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitPrompt(); } }}
                />
            }
            {inputError && <div className="tam-modal-input-error" data-testid="tam-modal-input-error">{inputError}</div>}
          </div>
        )}
        <div className="tam-modal-actions">
          {secondaryLabel && (
            <button
              type="button"
              className="tam-modal-btn secondary"
              data-testid="tam-modal-secondary"
              onClick={() => close(isPrompt ? null : false)}
            >{secondaryLabel}</button>
          )}
          <button
            ref={primaryRef}
            type="button"
            className={`tam-modal-btn primary ${danger ? "danger" : ""} ${current.tone ? `tf-tone-${current.tone}` : ""}`}
            data-testid="tam-modal-primary"
            style={current.primaryStyle || undefined}
            onClick={() => isPrompt ? submitPrompt() : close(true)}
          >{primaryLabel}</button>
        </div>
      </div>
    </div>
  );
}

export default notify;
